import { EventHandler, EventIds, EventLevel, RequestInfo, RequestType } from '@fpkg/stats-core';

import { getUrlPath, isUrlInIgnoreList } from '@fpkg/stats-utils';
import queryString from 'query-string';
import stringify from 'json-stringify-safe';

import { generateTraceId, isSameOrigin } from '../utils';

/* 请求监控配置 */
export interface AjaxOpt {
  blackList?: (string | RegExp)[]; // 忽略请求list，可以字符串和正则表达式
  validateHttpStatus?: (status: number) => boolean;
  validateJsonRes?: (result: any) => boolean; // 校验业务请求是否ok，只有json格式的响应才会调用。默认为：(result) => result.success === true
  headerAddTraceId?: boolean; // 请求头是否添加traceid
  crossOriginWhiteList?: Array<string | RegExp>; // 强制加sw8的域名白名单
  appCode?: string; // 大前端apm系统的appCode
  event_level?: EventLevel; // 这个事件级别设置字段只会修改服务器异常的请求类型，其他类型都是info
}

/**
 * 装换参数为字符串
 * @param params
 * @returns {any}
 */
const getParamsString = (params: any): string => {
  if (params === undefined || params === null) return '';
  if (typeof params === 'string') return params;
  return stringify(params);
};

function combineURLs(baseURL: string, relativeURL: string): string {
  return relativeURL ? `${baseURL.replace(/\/+$/, '')}/${relativeURL.replace(/^\/+/, '')}` : baseURL;
}

function isAbsoluteURL(url: string): boolean {
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}

const buildURL = (url: string, local?: Location): string => {
  if (isAbsoluteURL(url)) return url;
  return combineURLs(local ? local.origin : window.location.origin, url);
};

const isJsonResponse = (contentType: string | null): boolean => {
  if (!contentType) return false;
  return /application\/json/i.test(contentType);
};

const parseJsonResponse = (jsonStr: string): any | null => {
  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    return null;
  }
};

const parseLoadType = (xhr: XMLHttpRequest, requestOption: AjaxOpt): any => {
  const { status, responseType } = xhr;

  const requestOpt = { ...requestOption };

  if (!requestOpt.validateHttpStatus!(status)) {
    // status = 404、500等
    return {
      type: RequestType.server_error,
      error_type: '0',
      response_body: '',
    };
  }

  // status === 200
  const isJsonRes = isJsonResponse('getResponseHeader' in xhr ? xhr.getResponseHeader('content-type') : null);

  if (!isJsonRes) {
    // 非json格式响应定义为成功响应
    return {
      type: RequestType.ok,
      error_type: RequestType.ok,
      response_body: '', // 非json格式不上报body
    };
  }

  // json格式响应
  let response_body = '';
  try {
    response_body =
      !responseType || responseType === 'text' || responseType === 'json' ? xhr.responseText : xhr.response;
  } catch (e) {
    response_body = xhr.response || '';
  }
  const responseObj = parseJsonResponse(response_body);

  if (responseObj === null) {
    // json格式响应,json解析失败
    return {
      type: RequestType.json_error,
      error_type: RequestType.json_error,
      response_body,
    };
  }

  if (requestOpt.validateJsonRes && requestOpt.validateJsonRes(responseObj)) {
    // 业务响应ok
    return {
      type: RequestType.ok,
      error_type: RequestType.ok,
      response_body,
    };
  }

  // 业务响应失败
  return {
    type: RequestType.business_error,
    error_type: '1',
    response_body,
    error_code: responseObj.errorCode || responseObj.error_code,
    error_message: responseObj.errorMessage || responseObj.error_message,
  };
};

const isCrossOriginWhiteUrl = (url: string, whiteList: Array<string | RegExp>): boolean => {
  if (!whiteList || whiteList.length === 0) return false;
  return whiteList.some((item) => {
    if (typeof item === 'string') return url.includes(item);
    return item.test(url);
  });
};

/**
 * 监控请求异常
 */
export const captureXMLHttpRequest = (
  ajaxObj: typeof XMLHttpRequest,
  local: Location,
  requestOptiton: AjaxOpt & { ignoreList: Array<RegExp> },
  callback: EventHandler<RequestInfo>
) => {
  if (!ajaxObj || !callback) return;

  const requestOpt = { ...requestOptiton };
  const { ignoreList = [] } = requestOpt;

  const { open, send } = ajaxObj.prototype;

  // eslint-disable-next-line no-param-reassign
  ajaxObj.prototype.open = function xhrOpen(method: string, url: string | URL, async?: boolean) {
    const result = queryString.parseUrl(typeof url === 'string' ? url : url.href);
    const http_url = buildURL(result.url, local);
    this._ignore = isUrlInIgnoreList(http_url, ignoreList) || isUrlInIgnoreList(http_url, requestOpt.blackList);

    if (!this._ignore) {
      this._requestInfo = {
        event: EventIds.RequestInfo,
        type: RequestType.ok,
        traceid: '',
        http_url,
        http_path: getUrlPath(http_url),
        http_query: Object.keys(result.query).length === 0 ? '' : stringify(result.query),
        http_status: 0,
        http_method: method.toLowerCase(),
        error_type: '',
        http_params: '',
        duration: 0,
        $url: window.location.href, // 保存当前页面路径
        $title: document.title, // 保存当前页面title
      };
    }

    return open.call(this, method, url, typeof async === 'boolean' ? async : true);
  };

  // eslint-disable-next-line no-param-reassign
  ajaxObj.prototype.send = function xhrSend(...args) {
    if (this._ignore) return send.apply(this, args);

    let _this: any = this;
    const requestInfo: RequestInfo = _this._requestInfo;

    // 设置traceId
    const crossOriginWhiteList = requestOpt.crossOriginWhiteList || [];
    const canAddHeader = 'setRequestHeader' in this;
    const needAddHeader = () =>
      requestOpt.headerAddTraceId &&
      (isSameOrigin(requestInfo.http_url, local) || isCrossOriginWhiteUrl(requestInfo.http_url, crossOriginWhiteList));
    if (canAddHeader && needAddHeader()) {
      // appCode 可以为空，因为和后台关联的 traceid 用的是swa 第二段的随机字符串
      const sw8 = generateTraceId(requestOpt.appCode || '', requestInfo.http_url);
      this.setRequestHeader('sw8', sw8);
      requestInfo.traceid = sw8;
    }

    requestInfo.http_params = getParamsString(args[0]);
    const startTime = Date.now();

    // 超时
    this.addEventListener('timeout', () => {
      const { status } = _this;
      const endTime = Date.now();
      Object.assign(requestInfo, {
        type: RequestType.timeout,
        http_status: status,
        duration: endTime - startTime,
        error_type: RequestType.timeout,
        start_time: startTime,
        end_time: endTime,
      });
      callback(requestInfo);
      _this = null;
    });

    // 中断，取消
    this.addEventListener('abort', () => {
      if (!_this) return;

      const { status } = _this;
      const endTime = Date.now();
      Object.assign(requestInfo, {
        type: RequestType.abort,
        http_status: status,
        duration: endTime - startTime,
        error_type: RequestType.abort,
        end_time: endTime,
      });
      callback(requestInfo);
      _this = null;
    });

    // 网络错误：无网络、跨域错误
    this.addEventListener('error', () => {
      const { status } = _this;
      const endTime = Date.now();
      Object.assign(requestInfo, {
        type: RequestType.network_error,
        http_status: status,
        duration: endTime - startTime,
        error_type: RequestType.network_error,
        end_time: endTime,
      });
      callback(requestInfo);
      _this = null;
    });

    // 请求成功，200、404、500等
    // load事件在请求成功完成时触发，有些浏览器版本load事件触发时responseText还没完整数据，解析时会报错，参照axios改成监听loadend事件
    // loadend 事件总是在一个资源的加载进度停止之后被触发 (例如，在已经触发“error”，“abort”或“load”事件之后)
    this.addEventListener('loadend', () => {
      if (!_this) return;

      const { status } = _this;
      const { type, error_type, response_body, error_code = '', error_message = '' } = parseLoadType(_this, requestOpt);

      const endTime = Date.now();
      Object.assign(requestInfo, {
        type,
        response_body,
        error_code,
        error_message,
        http_status: status,
        duration: endTime - startTime,
        error_type,
        end_time: endTime,
      });
      callback(requestInfo);
      _this = null;
    });
    return send.apply(this, args);
  };
};
